import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectAuth } from '@/redux/auth/selectors';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { Card } from 'antd';
import NetRecurringBenefitChart from '@/pages/Dashboard/NetRecurringBenefitChart';
import OneTimeBenefitChart from '@/pages/Dashboard/OneTimeBenefitChart';
import OneTimeImplementationCostChart from '@/pages/Dashboard/OneTimeImplementationCostChart ';
import determineStage from '@/utils/DetermineStage';
import { getCancellationDate, getCancellationMonth, getCancellationQuarter, getCancellationWeek, getCancellationYear, getQualitativeKPI, getStageActualMonth, getStageActualQuarter, getStageActualWeek, getStageActualYear, getStageOriginalPlanMonth, getStageOriginalPlanQuarter, getStageOriginalPlanWeek, getStageOriginalPlanYear, getStagePlanMonth, getStagePlanQuarter, getStagePlanWeek, getStagePlanYear, L0ActualDate, L1ActualDate, L2ActualDate, L2PlanDate, L3ActualDate, L3PlanDate, L4ActualDate, L4PlanDate, L5ActualDate, L5PlanDate, SubmittedForL1PlanDate, SubmittedForL2PlanDate, SubmittedForL3PlanDate, SubmittedForL4PlanDate, SubmittedForL5PlanDate } from '@/utils/Sort/checkDate/getDate';
import checkWorkstreamLeadApprovalStatus from '@/utils/Sort/checkWorkstreamLeadApprovalStatus';
import checkWorkstreamSponsorApprovalStatus from '@/utils/Sort/checkWorkstreamSponsorApprovalStatus';
import { checkTOFinanceApprovalStatus, getComplianceStatusForGrouping, getNetRecurringBenefitCatagory, getNetRecurringBenefitCatagoryL0Plus, getNetRecurringBenefitCatagoryL1Plus, getNetRecurringBenefitCatagoryL2Plus, getNetRecurringBenefitCatagoryL3Plus, getNetRecurringBenefitCatagoryL4Plus, LLatestActualDate } from '@/utils/helpers/helpers';
import checkStrategyAnalystApprovalStatus from '@/utils/Sort/checkStrategyAnalystApprovalStatus';
import InitiativePlanningSummary from '@/utils/initiativePlanningSummary';
import planningSummarySimplified from '@/utils/planningSummarySimplified';
import getImplementationCostCatagory from '@/utils/Sort/getImplementationCostCatagory';
import sortInitiativesByActualImpact from '@/utils/Sort/sortByActualImpact';
import sortInitiativesByActuals from '@/utils/Sort/sortByActual';

// Default color palette for stages - ONLY L0 to L5
const DEFAULT_STAGE_COLORS = {
  'L0': '#FF6B6B', // Red
  'L1': '#4ECDC4', // Teal
  'L2': '#45B7D1', // Light blue
  'L3': '#96CEB4', // Mint green
  'L4': '#FECA57', // Yellow
  'L5': '#FF9FF3', // Pink
};

// Memoized helper functions
const memoizedDetermineStage = React.memo(determineStage);
const memoizedGetQualitativeKPI = React.memo(getQualitativeKPI);
const memoizedCheckWorkstreamLeadApprovalStatus = React.memo(checkWorkstreamLeadApprovalStatus);
const memoizedCheckWorkstreamSponsorApprovalStatus = React.memo(checkWorkstreamSponsorApprovalStatus);
const memoizedCheckTOFinanceApprovalStatus = React.memo(checkTOFinanceApprovalStatus);
const memoizedCheckStrategyAnalystApprovalStatus = React.memo(checkStrategyAnalystApprovalStatus);
const memoizedInitiativePlanningSummary = React.memo(InitiativePlanningSummary);
const memoizedPlanningSummarySimplified = React.memo(planningSummarySimplified);
const memoizedGetNetRecurringBenefitCatagory = React.memo(getNetRecurringBenefitCatagory);
const memoizedGetImplementationCostCatagory = React.memo(getImplementationCostCatagory);
const memoizedGetComplianceStatusForGrouping = React.memo(getComplianceStatusForGrouping);
const memoizedSortInitiativesByActualImpact = React.memo(sortInitiativesByActualImpact);
const memoizedSortInitiativesByActuals = React.memo(sortInitiativesByActuals);

// Helper function to safely sum values
const sumAnnualizedValue = (annualizedValue) => {
  if (typeof annualizedValue === 'number') {
    return annualizedValue;
  }
  if (Array.isArray(annualizedValue)) {
    return annualizedValue.reduce((sum, value) => sum + (value || 0), 0);
  }
  return 0;
};

// Precompute common values
const NET_RECURRING_BENEFIT_YAXIS = 'Net Recurring Benefits (Planned, by stage, ETB Mn)';
const ONE_TIME_BENEFIT_YAXIS = "One Time Benefits (Calendarized, Planned, by Stage, ETB Mn)";
const ONE_TIME_IMPLEMENTATION_COST_YAXIS = "One Time Implementation Costs (Planned, ETB Mn)";

const StackedChart = ({ initiatives, selectedGroup, yAxis, attributeFilters, colorByAttribute, initiativeColors }) => {
  const currentUser = useSelector(selectAuth);

  // Merge default colors with custom initiativeColors
  const mergedColors = useMemo(() => {
    return { ...DEFAULT_STAGE_COLORS, ...initiativeColors };
  }, [initiativeColors]);

  // Early return for special chart types
  if (yAxis === NET_RECURRING_BENEFIT_YAXIS) {
    return <NetRecurringBenefitChart
      initiatives={initiatives}
      initiativeColors={mergedColors}
      colorByAttribute={colorByAttribute}
      selectedGroup={selectedGroup}
    />;
  }
  if (yAxis === ONE_TIME_BENEFIT_YAXIS) {
    return <OneTimeBenefitChart
      initiatives={initiatives}
      initiativeColors={mergedColors}
      colorByAttribute={colorByAttribute}
      selectedGroup={selectedGroup}
    />;
  }
  if (yAxis === ONE_TIME_IMPLEMENTATION_COST_YAXIS) {
    return <OneTimeImplementationCostChart
      initiatives={initiatives}
      initiativeColors={mergedColors}
      colorByAttribute={colorByAttribute}
      selectedGroup={selectedGroup}
    />;
  }

  // Memoized calculateTotal function
  const calculateTotal = useCallback((initiatives, attribute) => {
    if (attribute === 'Number of Initiatives') {
      return initiatives.length;
    }
    if (attribute === 'Net Recurring Benefit') {
      return initiatives.reduce((sum, initiative) => {
        let recurringBenefit = 0;
        let negativeRecurringImpact = 0;
        const impacts = initiative.impact || [];

        for (let i = 0; i < impacts.length; i++) {
          const impact = impacts[i];
          if (impact.frequencyAndDirection?.startsWith('Recurring')) {
            const annualizedPlanSum = sumAnnualizedValue(impact.annualizedValues?.plan);
            if (impact.frequencyAndDirection.includes('Benefit')) {
              recurringBenefit += annualizedPlanSum;
            } else if (impact.frequencyAndDirection.includes('Negative')) {
              negativeRecurringImpact += annualizedPlanSum;
            }
          }
        }
        return sum + recurringBenefit - negativeRecurringImpact;
      }, 0);
    }
    return 0;
  }, []);

  // Memoized user permission check
  const hasUserPermission = useCallback((initiative) => {
    if (currentUser?.hasSpecialAccess === true) {
      return true;
    }
    return (
      initiative.initiativeOwnerInfo?.[0]?._id === currentUser.id ||
      initiative.initiativeOwnerBackupInfo?.[0]?._id === currentUser.id ||
      initiative.workstreamLeadInfo?.[0]?._id === currentUser.id ||
      (initiative.milestones?.some((milestone) => milestone.Owner === currentUser.id)) ||
      initiative.workstreamSponsorInfo?.[0]?._id === currentUser.id ||
      (initiative.AllowOnlyTo?.some((allowedUser) => allowedUser._id === currentUser.id)) ||
      ['CTO', 'TO Finance', 'Strategy Analyst', 'Admin'].includes(currentUser.role?.name)
    );
  }, [currentUser.id, currentUser.role?.name]);

  // Memoized attribute filter check
  const passesAttributeFilters = useCallback((initiative) => {
    if (!attributeFilters || Object.keys(attributeFilters).length === 0) {
      return true;
    }

    for (const [filterKey, filterValues] of Object.entries(attributeFilters)) {
      if (filterValues.length === 0) continue;

      let initiativeValue;
      switch (filterKey) {
        case 'stage':
        case 'stageSimplified':
          initiativeValue = initiative.stage;
          if (!filterValues.includes(initiativeValue)) return false;
          return true; // Added return statement
        case 'workstreamSponsorWorkstream':
          const workstreamNames = initiative.workstreamSponsorWorkstream?.map(ws => ws.workstreamName) || [];
          if (!workstreamNames.some(name => filterValues.includes(name))) return false;
          return true; // Added return statement
        case 'weeklyStatus':
          initiativeValue = initiative.weeklyStatus;
          if (!filterValues.includes(initiativeValue)) return false;
          return true; // Added return statement
        default:
          initiativeValue = initiative[filterKey];
          const matches = Array.isArray(initiativeValue)
            ? initiativeValue.some(val => filterValues.includes(val))
            : filterValues.includes(initiativeValue);
          if (!matches) return false;
          return true; // Added return statement
      }
    }
    return true;
  }, [attributeFilters]);

  // Optimized filtered initiatives
  const filteredInitiatives = useMemo(() => {
    const result = [];
    for (let i = 0; i < initiatives.length; i++) {
      const initiative = initiatives[i];
      if (hasUserPermission(initiative) && passesAttributeFilters(initiative)) {
        result.push(initiative);
      }
    }
    return result;
  }, [initiatives, hasUserPermission, passesAttributeFilters]);

  // Handle case when no group is selected - show total initiatives
  const shouldShowTotal = !selectedGroup || selectedGroup === 'None';

  // Precompute all stacking values
  const allStackingValues = useMemo(() => {
    const values = new Set();
    for (let i = 0; i < initiatives.length; i++) {
      if (initiatives[i].stage) {
        values.add(initiatives[i].stage);
      }
    }
    return Array.from(values).sort();
  }, [initiatives]);

  // Memoized grouping function (only used when group is selected)
  const getGroupKey = useCallback((initiative) => {
    switch (selectedGroup) {
      case 'initiativeOwnerInfo':
      case 'workstreamLeadInfo':
      case 'workstreamSponsorInfo':
      case 'initiativeOwnerBackupInfo':
      case 'AllowOnlyTo':
        const person = initiative[selectedGroup]?.[0];
        return person ? `${person.firstName} ${person.lastName}` : 'Unknown';

      case 'workstreamSponsorWorkstream':
        return initiative[selectedGroup]?.[0]?.workstreamName || 'Unknown';

      case 'workstreamLeadSubworkstream':
        return initiative[selectedGroup]?.[0]?.subworkstreamName || 'Unknown';

      case 'stage':
        return determineStage(initiative);

      case 'Stage (simplified)':
        return initiative.stage;

      case 'selectedForToMeetingDescusion':
      case 'hasFinancialImpact':
        return initiative[selectedGroup] ? 'Yes' : 'No';

      case 'Has qualitative KPI?':
        return getQualitativeKPI(initiative);

      case 'initiativeNumber - initiativeName':
        return `${initiative.initiativeNumber} - ${initiative.initiativeName}`;

      case 'workstreamLeadPendingApproval':
        return checkWorkstreamLeadApprovalStatus(initiative);

      case 'workstreamSponsorPendingApproval':
        return checkWorkstreamSponsorApprovalStatus(initiative);

      case 'financeLeadPendingApproval':
        return checkTOFinanceApprovalStatus(initiative);

      case 'Strategy analyst pending approval?':
        return checkStrategyAnalystApprovalStatus(initiative);

      case 'planningSummary':
        return InitiativePlanningSummary(initiative).planningStatus;

      case 'Planning summary (simplified)':
        return planningSummarySimplified(initiative);

      case 'netRecurringBenefitsRange':
        return getNetRecurringBenefitCatagory(initiative);

      case 'Future implementation costs range':
        return getImplementationCostCatagory(initiative);
      case 'l2ForecastDate':
        return L2PlanDate(initiative);
      case 'L3 date compliance check (forecast vs planned date)':
        return getComplianceStatusForGrouping(initiative, 'L3');

      case 'L4 date compliance check (forecast vs planned date)':
        return getComplianceStatusForGrouping(initiative, 'L4');

      case 'Number of Attachment':
        return initiative.attachmentCount;

      case 'actualImpactUpdateCheck':
      case 'actualKpiUpdateCheck':
        return sortInitiativesByActualImpact(initiative, selectedGroup);

      case '(FY24-25) Actual impact update check':
      case '(FY23-24) Actual impact update check':
        return sortInitiativesByActuals(initiative, selectedGroup);

      case 'cancellationDate':
        return getCancellationDate(initiative);

      case 'l3PlannedDate':
        return L3PlanDate(initiative);
      case 'l4PlannedDate':
        return L4PlanDate(initiative);
      case 'l5LatestEstimatedDate':
      case 'l5ForecastDate':
        return L5PlanDate(initiative);
      case 'l0ActualDate':
        return L0ActualDate(initiative);
      case 'l1ActualDate':
        return L1ActualDate(initiative);
      case 'l2ActualDate':
        return L2ActualDate(initiative);
      case 'l3ActualDate':
        return L3ActualDate(initiative);
      case 'l4ActualDate':
        return L4ActualDate(initiative);
      // L5 Actual Date
      case 'l5ActualDate':
        return L5ActualDate(initiative);
      case 'latestActualDateAllLGates':
        return LLatestActualDate(initiative);
      // Submitted for L1 Approval
      case 'subForL1ApprovDate':
        return SubmittedForL1PlanDate(initiative);
      // Repeat similarly for Submitted for L2, L3, L4, L5 Approval
      // Example for L2:
      case 'subForL2ApprovDate':
        return SubmittedForL2PlanDate(initiative);

      case 'subForL3ApprovDate':
        return SubmittedForL3PlanDate(initiative);
      case 'subForL4ApprovDate':
        return SubmittedForL4PlanDate(initiative);
      case 'subForL5ApprovDate':
        return SubmittedForL5PlanDate(initiative);
      case 'subForL5ApprovDate_Year':
        return getStageActualYear(initiative, 'Submitted for L5 Approval');
      case 'subForL5ApprovDate_Quarter':
        return getStageActualQuarter(initiative, 'Submitted for L5 Approval');
      case 'subForL5ApprovDate_Month':
        return getStageActualMonth(initiative, 'Submitted for L5 Approval');
      case 'subForL5ApprovDate_Week':
        return getStageActualWeek(initiative, 'Submitted for L5 Approval');
      case 'pipelineWeeklyVarianceL0Plus':
        return getNetRecurringBenefitCatagoryL0Plus(initiative);
      case 'pipelineWeeklyVarianceL1Plus':
        return getNetRecurringBenefitCatagoryL1Plus(initiative);
      case 'pipelineWeeklyVarianceL2Plus':
        return getNetRecurringBenefitCatagoryL2Plus(initiative);
      case 'pipelineWeeklyVarianceL3Plus':
        return getNetRecurringBenefitCatagoryL3Plus(initiative);
      case 'pipelineWeeklyVarianceL4Plus':
        return getNetRecurringBenefitCatagoryL4Plus(initiative);
      default:
        if (selectedGroup.startsWith('l0ActualDate')) {
          if (selectedGroup.endsWith('_Day')) {
            return L0ActualDate(initiative);
          } else if (selectedGroup.endsWith('_Year')) {
            return getStageActualYear(initiative, 'L0');
          }
          else if (selectedGroup.endsWith('_Quarter')) {
            return getStageActualQuarter(initiative, 'L0');
          }
          else if (selectedGroup.endsWith('_Month')) {
            return getStageActualMonth(initiative, 'L0');
          }
          else if (selectedGroup.endsWith('_Week')) {
            return getStageActualWeek(initiative, 'L0');
          }
        }
        else if (selectedGroup.startsWith('l5LatestEstimatedDate') || selectedGroup.startsWith('l5ForecastDate')) {
          if (selectedGroup.endsWith('_Day')) {
            return L5PlanDate(initiative);
          } else if (selectedGroup.endsWith('_Year')) {
            return getStagePlanYear(initiative, 'L5');
          }
          else if (selectedGroup.endsWith('_Quarter')) {
            return getStagePlanQuarter(initiative, 'L5');
          }
          else if (selectedGroup.endsWith('_Month')) {
            return getStagePlanMonth(initiative, 'L5');
          }
          else if (selectedGroup.endsWith('_Week')) {
            return getStagePlanWeek(initiative, 'L5');
          }
        }
        else if (selectedGroup.startsWith('l4PlannedDate')) {
          if (selectedGroup.endsWith('_Day')) {
            return L4PlanDate(initiative);
          } else if (selectedGroup.endsWith('_Year')) {
            return getStagePlanYear(initiative, 'L4');
          }
          else if (selectedGroup.endsWith('_Quarter')) {
            return getStagePlanQuarter(initiative, 'L4');
          }
          else if (selectedGroup.endsWith('_Month')) {
            return getStagePlanMonth(initiative, 'L4');
          }
          else if (selectedGroup.endsWith('_Week')) {
            return getStagePlanWeek(initiative, 'L4');
          }
        }
        else if (selectedGroup.startsWith('l3PlannedDate')) {
          if (selectedGroup.endsWith('_Day')) {
            return L3PlanDate(initiative);
          } else if (selectedGroup.endsWith('_Year')) {
            return getStagePlanYear(initiative, 'L3');
          }
          else if (selectedGroup.endsWith('_Quarter')) {
            return getStagePlanQuarter(initiative, 'L3');
          }
          else if (selectedGroup.endsWith('_Month')) {
            return getStagePlanMonth(initiative, 'L3');
          }
          else if (selectedGroup.endsWith('_Week')) {
            return getStagePlanWeek(initiative, 'L3');
          }
        }
        else if (selectedGroup.startsWith('l2ForecastDate')) {
          if (selectedGroup.endsWith('_Day')) {
            return L2PlanDate(initiative);
          } else if (selectedGroup.endsWith('_Year')) {
            return getStagePlanYear(initiative, 'L2');
          }
          else if (selectedGroup.endsWith('_Quarter')) {
            return getStagePlanQuarter(initiative, 'L2');
          }
          else if (selectedGroup.endsWith('_Month')) {
            return getStagePlanMonth(initiative, 'L2');
          }
          else if (selectedGroup.endsWith('_Week')) {
            return getStagePlanWeek(initiative, 'L2');
          }
        }
        else if (selectedGroup.startsWith('l1ActualDate')) {
          if (selectedGroup.endsWith('_Day')) {
            return L1ActualDate(initiative);
          } else if (selectedGroup.endsWith('_Year')) {
            return getStageActualYear(initiative, 'L1');
          }
          else if (selectedGroup.endsWith('_Quarter')) {
            return getStageActualQuarter(initiative, 'L1');
          }
          else if (selectedGroup.endsWith('_Month')) {
            return getStageActualMonth(initiative, 'L1');
          }
          else if (selectedGroup.endsWith('_Week')) {
            return getStageActualWeek(initiative, 'L1');
          }
        }
        else if (selectedGroup.startsWith('l2ActualDate')) {
          if (selectedGroup.endsWith('_Day')) {
            return L2ActualDate(initiative);
          } else if (selectedGroup.endsWith('_Year')) {
            return getStageActualYear(initiative, 'L2');
          }
          else if (selectedGroup.endsWith('_Quarter')) {
            return getStageActualQuarter(initiative, 'L2');
          }
          else if (selectedGroup.endsWith('_Month')) {
            return getStageActualMonth(initiative, 'L2');
          }
          else if (selectedGroup.endsWith('_Week')) {
            return getStageActualWeek(initiative, 'L2');
          }
        }
        else if (selectedGroup.startsWith('l3ActualDate')) {
          if (selectedGroup.endsWith('_Day')) {
            return L3ActualDate(initiative);
          } else if (selectedGroup.endsWith('_Year')) {
            return getStageActualYear(initiative, 'L3');
          }
          else if (selectedGroup.endsWith('_Quarter')) {
            return getStageActualQuarter(initiative, 'L3');
          }
          else if (selectedGroup.endsWith('_Month')) {
            return getStageActualMonth(initiative, 'L3');
          }
          else if (selectedGroup.endsWith('_Week')) {
            return getStageActualWeek(initiative, 'L3');
          }
        }
        else if (selectedGroup.startsWith('latestActualDateAllLGates')) {
          if (selectedGroup.endsWith('_Day')) {
            return LLatestActualDate(initiative);
          } else if (selectedGroup.endsWith('_Year')) {
            return getStageActualYear(initiative, 'AllLGates');
          }
          else if (selectedGroup.endsWith('_Quarter')) {
            return getStageActualQuarter(initiative, 'AllLGates');
          }
          else if (selectedGroup.endsWith('_Month')) {
            return getStageActualMonth(initiative, 'AllLGates');
          }
          else if (selectedGroup.endsWith('_Week')) {
            return getStageActualWeek(initiative, 'AllLGates');
          }
        }
        else if (selectedGroup.startsWith('l4ActualDate')) {
          if (selectedGroup.endsWith('_Day')) {
            return L4ActualDate(initiative);
          } else if (selectedGroup.endsWith('_Year')) {
            return getStageActualYear(initiative, 'L4');
          }
          else if (selectedGroup.endsWith('_Quarter')) {
            return getStageActualQuarter(initiative, 'L4');
          }
          else if (selectedGroup.endsWith('_Month')) {
            return getStageActualMonth(initiative, 'L4');
          }
          else if (selectedGroup.endsWith('_Week')) {
            return getStageActualWeek(initiative, 'L4');
          }
        }
        else if (selectedGroup?.startsWith('L1 Original Planned Date')) {
          if (selectedGroup?.endsWith('_Day')) {
            return L1OriginalPlanDate(initiative);
          } else if (selectedGroup?.endsWith('_Year')) {
            return getStageOriginalPlanYear(initiative, 'L1');
          }
          else if (selectedGroup?.endsWith('_Quarter')) {
            return getStageOriginalPlanQuarter(initiative, 'L1');
          }
          else if (selectedGroup?.endsWith('_Month')) {
            return getStageOriginalPlanMonth(initiative, 'L1');
          }
          else if (selectedGroup?.endsWith('_Week')) {
            return getStageOriginalPlanWeek(initiative, 'L1');
          }
        }
        else if (selectedGroup?.startsWith('L2 Original Planned Date')) {
          if (selectedGroup?.endsWith('_Day')) {
            return L2OriginalPlanDate(initiative);
          } else if (selectedGroup?.endsWith('_Year')) {
            return getStageOriginalPlanYear(initiative, 'L2');
          }
          else if (selectedGroup?.endsWith('_Quarter')) {
            return getStageOriginalPlanQuarter(initiative, 'L2');
          }
          else if (selectedGroup?.endsWith('_Month')) {
            return getStageOriginalPlanMonth(initiative, 'L2');
          }
          else if (selectedGroup?.endsWith('_Week')) {
            return getStageOriginalPlanWeek(initiative, 'L2');
          }
        }
        else if (selectedGroup?.startsWith('L3 Original Planned Date')) {
          if (selectedGroup?.endsWith('_Day')) {
            return L3OriginalPlanDate(initiative);
          } else if (selectedGroup?.endsWith('_Year')) {
            return getStageOriginalPlanYear(initiative, 'L3');
          }
          else if (selectedGroup?.endsWith('_Quarter')) {
            return getStageOriginalPlanQuarter(initiative, 'L3');
          }
          else if (selectedGroup?.endsWith('_Month')) {
            return getStageOriginalPlanMonth(initiative, 'L3');
          }
          else if (selectedGroup?.endsWith('_Week')) {
            return getStageOriginalPlanWeek(initiative, 'L3');
          }
        }
        else if (selectedGroup?.startsWith('L4 Original Planned Date')) {
          if (selectedGroup?.endsWith('_Day')) {
            return L4OriginalPlanDate(initiative);
          } else if (selectedGroup?.endsWith('_Year')) {
            return getStageOriginalPlanYear(initiative, 'L4');
          }
          else if (selectedGroup?.endsWith('_Quarter')) {
            return getStageOriginalPlanQuarter(initiative, 'L4');
          }
          else if (selectedGroup?.endsWith('_Month')) {
            return getStageOriginalPlanMonth(initiative, 'L4');
          }
          else if (selectedGroup?.endsWith('_Week')) {
            return getStageOriginalPlanWeek(initiative, 'L4');
          }
        }
        else if (selectedGroup?.startsWith('L5 Original Planned Date')) {
          if (selectedGroup?.endsWith('_Day')) {
            return L5OriginalPlanDate(initiative);
          } else if (selectedGroup?.endsWith('_Year')) {
            return getStageOriginalPlanYear(initiative, 'L5');
          }
          else if (selectedGroup?.endsWith('_Quarter')) {
            return getStageOriginalPlanQuarter(initiative, 'L5');
          }
          else if (selectedGroup?.endsWith('_Month')) {
            return getStageOriginalPlanMonth(initiative, 'L5');
          }
          else if (selectedGroup?.endsWith('_Week')) {
            return getStageOriginalPlanWeek(initiative, 'L5');
          }
        }
        else if (selectedGroup.startsWith('l5ActualDate')) {
          if (selectedGroup.endsWith('_Day')) {
            return L5ActualDate(initiative);
          } else if (selectedGroup.endsWith('_Year')) {
            return getStageActualYear(initiative, 'L5');
          }
          else if (selectedGroup.endsWith('_Quarter')) {
            return getStageActualQuarter(initiative, 'L5');
          }
          else if (selectedGroup.endsWith('_Month')) {
            return getStageActualMonth(initiative, 'L5');
          }
          else if (selectedGroup.endsWith('_Week')) {
            return getStageActualWeek(initiative, 'L5');
          }
        }
        else if (selectedGroup.startsWith('subForL1ApprovDate')) {
          if (selectedGroup.endsWith('_Day')) {
            return SubmittedForL1PlanDate(initiative);
          } else if (selectedGroup.endsWith('_Year')) {
            return getStageActualYear(initiative, 'Submitted for L1 Approval');
          }
          else if (selectedGroup.endsWith('_Quarter')) {
            return getStageActualQuarter(initiative, 'Submitted for L1 Approval');
          }
          else if (selectedGroup.endsWith('_Month')) {
            return getStageActualMonth(initiative, 'Submitted for L1 Approval');
          }
          else if (selectedGroup.endsWith('_Week')) {
            return getStageActualWeek(initiative, 'Submitted for L1 Approval');
          }
        }
        else if (selectedGroup.startsWith('subForL2ApprovDate')) {
          if (selectedGroup.endsWith('_Day')) {
            return SubmittedForL2PlanDate(initiative);
          } else if (selectedGroup.endsWith('_Year')) {
            return getStageActualYear(initiative, 'Submitted for L2 Approval');
          }
          else if (selectedGroup.endsWith('_Quarter')) {
            return getStageActualQuarter(initiative, 'Submitted for L2 Approval');
          }
          else if (selectedGroup.endsWith('_Month')) {
            return getStageActualMonth(initiative, 'Submitted for L2 Approval');
          }
          else if (selectedGroup.endsWith('_Week')) {
            return getStageActualWeek(initiative, 'Submitted for L2 Approval');
          }
        }
        else if (selectedGroup.startsWith('subForL3ApprovDate')) {
          if (selectedGroup.endsWith('_Day')) {
            return SubmittedForL3PlanDate(initiative);
          } else if (selectedGroup.endsWith('_Year')) {
            return getStageActualYear(initiative, 'Submitted for L3 Approval');
          }
          else if (selectedGroup.endsWith('_Quarter')) {
            return getStageActualQuarter(initiative, 'Submitted for L3 Approval');
          }
          else if (selectedGroup.endsWith('_Month')) {
            return getStageActualMonth(initiative, 'Submitted for L3 Approval');
          }
          else if (selectedGroup.endsWith('_Week')) {
            return getStageActualWeek(initiative, 'Submitted for L3 Approval');
          }
        }
        else if (selectedGroup.startsWith('subForL4ApprovDate')) {
          if (selectedGroup.endsWith('_Day')) {
            return SubmittedForL4PlanDate(initiative);
          } else if (selectedGroup.endsWith('_Year')) {
            return getStageActualYear(initiative, 'Submitted for L4 Approval');
          }
          else if (selectedGroup.endsWith('_Quarter')) {
            return getStageActualQuarter(initiative, 'Submitted for L4 Approval');
          }
          else if (selectedGroup.endsWith('_Month')) {
            return getStageActualMonth(initiative, 'Submitted for L4 Approval');
          }
          else if (selectedGroup.endsWith('_Week')) {
            return getStageActualWeek(initiative, 'Submitted for L4 Approval');
          }
        }
        else if (selectedGroup.startsWith('subForL5ApprovDate')) {
          if (selectedGroup.endsWith('_Day')) {
            return SubmittedForL1PlanDate(initiative);
          } else if (selectedGroup.endsWith('_Year')) {
            return getStageActualYear(initiative, 'Submitted for L5 Approval');
          }
          else if (selectedGroup.endsWith('_Quarter')) {
            return getStageActualQuarter(initiative, 'Submitted for L5 Approval');
          }
          else if (selectedGroup.endsWith('_Month')) {
            return getStageActualMonth(initiative, 'Submitted for L5 Approval');
          }
          else if (selectedGroup.endsWith('_Week')) {
            return getStageActualWeek(initiative, 'Submitted for L5 Approval');
          }
        }
        else if (selectedGroup.startsWith('cancellationDate')) {
          if (selectedGroup?.endsWith('_Day')) {
            return getCancellationDate(initiative);
          } else if (selectedGroup?.endsWith('_Year')) {
            return getCancellationYear(initiative);
          }
          else if (selectedGroup?.endsWith('_Quarter')) {
            return getCancellationQuarter(initiative);
          }
          else if (selectedGroup?.endsWith('_Month')) {
            return getCancellationMonth(initiative);
          }
          else if (selectedGroup?.endsWith('_Week')) {
            return getCancellationWeek(initiative);
          }
        }
        else {
          return initiative[selectedGroup] || 'Unknown';
        }
        break;
    }
  }, [selectedGroup]);
  const chartData = useMemo(() => {
    if (shouldShowTotal) {
      const entry = { group: 'Total Initiatives' };
      let total = 0;

      for (let j = 0; j < allStackingValues.length; j++) {
        const stackingValue = allStackingValues[j];
        const initiativesInStack = filteredInitiatives.filter(i => i.stage === stackingValue);
        const value = calculateTotal(initiativesInStack, yAxis);
        entry[stackingValue] = value;
        total += value;
      }

      entry.total = total;
      return [entry];
    }

    const groupedByXAxis = new Map();

    // Group initiatives
    for (let i = 0; i < filteredInitiatives.length; i++) {
      const initiative = filteredInitiatives[i];
      const key = getGroupKey(initiative);

      if (!groupedByXAxis.has(key)) {
        groupedByXAxis.set(key, []);
      }
      groupedByXAxis.get(key).push(initiative);
    }

    const result = [];

    // Calculate values for each group
    for (const [xAxisLabel, initiativesInGroup] of groupedByXAxis) {
      const entry = { [selectedGroup]: xAxisLabel };
      let total = 0;

      for (let j = 0; j < allStackingValues.length; j++) {
        const stackingValue = allStackingValues[j];
        const initiativesInStack = initiativesInGroup.filter(i => i.stage === stackingValue);
        const value = calculateTotal(initiativesInStack, yAxis);
        entry[stackingValue] = value;
        total += value;
      }

      entry.total = total;
      result.push(entry);
    }

    return result;
  }, [filteredInitiatives, selectedGroup, yAxis, getGroupKey, allStackingValues, calculateTotal, shouldShowTotal]);

  // Memoized bars with default colors
  const bars = useMemo(() => {
    return allStackingValues.map(value => (
      <Bar
        key={value}
        dataKey={value}
        stackId="a"
        fill={mergedColors[value] || '#8884d8'} // Fallback to default if stage not found
      >
        <LabelList
          dataKey={value}
          position="inside"
          formatter={(val) => {
            if (val === 0) return '';
            if (val > 1000000) return `${(val / 1000000).toFixed(3)}M`;
            if (val > 1000) return `${(val / 1000).toFixed(3)}K`;
            return val.toFixed(3);
          }}
          style={{
            fill: '#fff',
            fontSize: '12px',
            fontWeight: 'bold',
            textShadow: '1px 1px 2px rgba(0,0,0,0.7)' // Better contrast
          }}
        />
      </Bar>
    ));
  }, [allStackingValues, mergedColors]);

  // Custom legend formatter for better readability
  const renderLegend = useCallback((props) => {
    const { payload } = props;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px' }}>
        {payload.map((entry, index) => (
          <div key={`legend-${index}`} style={{ display: 'flex', alignItems: 'center', margin: '0 5px' }}>
            <div
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: entry.color,
                marginRight: '5px',
                borderRadius: '2px'
              }}
            />
            <span style={{ fontSize: '12px' }}>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  }, []);

  const chartTitle = shouldShowTotal
    ? `${yAxis} - Total Initiatives`
    : `${yAxis} by ${selectedGroup}`;

  return (
    <Card
      title={<span style={{ color: '#0078D4' }}>{chartTitle}</span>}
      style={{ width: '100%' }}
    >
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: shouldShowTotal ? 20 : 5, // More bottom margin for single bar
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey={shouldShowTotal ? 'group' : selectedGroup}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis />
          <Tooltip />
          <Legend content={renderLegend} />
          {bars}
          <Bar dataKey="total" fill="transparent">
            <LabelList
              dataKey="total"
              position="top"
              formatter={(val) => {
                if (val === 0) return '';
                if (val > 1000000) return `${(val / 1000000).toFixed(3)}M`;
                if (val > 1000) return `${(val / 1000).toFixed(3)}K`;
                return val.toFixed(3);
              }}
              style={{
                fill: '#000',
                fontSize: '12px',
                fontWeight: 'bold',
                textShadow: '1px 1px 2px rgba(255,255,255,0.8)' // Better visibility
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
};

export default React.memo(StackedChart);